import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
      this.addEventListener('submit', this.#handleAddToCartSubmit, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const videoButton = target.closest('.gpc__video-btn');
    if (videoButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();

      const source = videoButton.dataset.videoSrc;
      const videoType = videoButton.dataset.videoType;
      const productTitle = videoButton.dataset.productTitle || '';
      const productPrice = videoButton.dataset.productPrice || '';
      const variantId = videoButton.dataset.variantId || '';
      if (!source) return;

      this.#openSearchVideoModal(source, videoType || 'video', productTitle, productPrice, variantId);
      return;
    }

    const modalAtcButton = target.closest('[data-gpc-modal-atc]');
    if (modalAtcButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#addModalVariantToCart(modalAtcButton);
      return;
    }

    if (target.closest('[data-gpc-modal-close]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#closeSearchVideoModal();
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Handle ATC from product cards in search, add via AJAX and open cart drawer.
   * @param {SubmitEvent} event
   */
  #handleAddToCartSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!event.target.matches('form[data-gpc-atc]')) return;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const submitButton = form.querySelector('.gpc__atc-btn');
    const originalLabel = submitButton instanceof HTMLButtonElement ? submitButton.innerHTML : null;

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.innerHTML = '<span>Adding...</span>';
    }

    try {
      const formData = new FormData(form);

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: new URLSearchParams(formData).toString(),
      });

      if (!addResponse.ok) {
        throw new Error('Unable to add item to cart');
      }

      const cartResponse = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: {
              source: 'predictive-search',
            },
          },
        })
      );

      const cartDrawer = document.querySelector('cart-drawer-component');
      cartDrawer?.open();

      if (submitButton instanceof HTMLButtonElement) {
        submitButton.innerHTML = '<span>Added!</span>';
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        setTimeout(() => {
          submitButton.disabled = false;
          if (originalLabel != null) {
            submitButton.innerHTML = originalLabel;
          }
        }, 900);
      }
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.key === 'Escape' && this.#isSearchVideoModalOpen()) {
      this.#closeSearchVideoModal();
      return;
    }

    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#closeSearchVideoModal();
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        this.#setPopularSearchFallbackVisibility(true);
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.setAttribute('data-has-query', 'true');
    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.removeAttribute('data-has-query');
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    this.#setPopularSearchFallbackVisibility(viewedProducts.length === 0, parsedEmptySectionMarkup);

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };

  /**
   * Toggle popular-search fallback area.
   * @param {boolean} show
   * @param {ParentNode} [root]
   */
  #setPopularSearchFallbackVisibility(show, root = this.refs.predictiveSearchResults) {
    const popularSearchFallback = root.querySelector('[data-popular-search-fallback]');
    if (popularSearchFallback instanceof HTMLElement) {
      popularSearchFallback.hidden = !show;
    }
  }

  #openSearchVideoModal(source, videoType, productTitle, productPrice, variantId) {
    const modal = this.refs.gpcVideoModal;
    const content = this.refs.gpcVideoModalContent;
    const title = this.refs.gpcVideoModalTitle;
    const price = this.refs.gpcVideoModalPrice;
    const atc = this.refs.gpcVideoModalAtc;
    if (!(modal instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    if (videoType === 'iframe') {
      content.innerHTML = `<iframe src="${source}" frameborder="0" allowfullscreen allow="autoplay; encrypted-media"></iframe>`;
    } else {
      content.innerHTML = `<video src="${source}" controls autoplay muted playsinline></video>`;
    }

    if (title instanceof HTMLElement) title.textContent = productTitle;
    if (price instanceof HTMLElement) price.textContent = productPrice;
    if (atc instanceof HTMLButtonElement) atc.dataset.variantId = variantId;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
  }

  #closeSearchVideoModal() {
    const modal = this.refs.gpcVideoModal;
    const content = this.refs.gpcVideoModalContent;
    const title = this.refs.gpcVideoModalTitle;
    const price = this.refs.gpcVideoModalPrice;
    const atc = this.refs.gpcVideoModalAtc;
    if (!(modal instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    content.innerHTML = '';
    if (title instanceof HTMLElement) title.textContent = '';
    if (price instanceof HTMLElement) price.textContent = '';
    if (atc instanceof HTMLButtonElement) delete atc.dataset.variantId;
  }

  #isSearchVideoModalOpen() {
    const modal = this.refs.gpcVideoModal;
    return modal instanceof HTMLElement && modal.hidden === false;
  }

  /**
   * Add current modal variant to cart and open drawer.
   * @param {HTMLButtonElement} button
   */
  async #addModalVariantToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: new URLSearchParams({ id: variantId, quantity: '1' }).toString(),
      });

      if (!addResponse.ok) {
        throw new Error('Unable to add item to cart');
      }

      const cartResponse = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: { source: 'predictive-search-video-modal' },
          },
        })
      );

      const cartDrawer = document.querySelector('cart-drawer-component');
      cartDrawer?.open();
      this.#closeSearchVideoModal();
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
      button.textContent = originalLabel || 'Add To Cart';
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
